import React, { useState, useEffect } from 'react';
import { Row, Col, Menu, Typography, Card, Spin } from 'antd';
import {
    FileSearchOutlined,
    TableOutlined,
    CloudUploadOutlined,
    ShareAltOutlined,
    CloudDownloadOutlined,
} from '@ant-design/icons';

import { useLocation, useNavigate } from 'react-router-dom'
import HomeSearchResult from './HomeSearchResult';
import HomeUpload from './HomeUpload';
import AuditPendingNotice from '../../components/AuditPendingNotice';
import { refreshCurrentProfile } from '../../utils/authProfile';

const { Title, Text } = Typography;

export default function Home() {

    const location = useLocation();
    const initialPage = location.state?.pages;

    const navigate = useNavigate();
    const [downPage, setDownPage] = useState(initialPage || 'search');
    const [current, setCurrent] = useState(initialPage || 'search');
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileState, setProfileState] = useState({
        status: localStorage.getItem('status'),
    });
    const userInfo = localStorage.getItem('userInfo');
    const accountStatus = String(profileState.status ?? '');
    const isAuditPending = userInfo && ['0', '2'].includes(accountStatus);

    useEffect(() => {
        if (!userInfo) {
            // 直接访问 Imputation 时记录目标页，登录后回到提交页面
            navigate('/login', { state: { from: '/home' } })
            console.log('Data does not exist in localStorage');
            return;
        }
        setProfileLoading(true);
        refreshCurrentProfile()
            .then((profile) => {
                setProfileState({
                    status: profile.status,
                });
            })
            .catch(() => {
                navigate('/login', { state: { from: '/home' } });
            })
            .finally(() => setProfileLoading(false));
    }, [navigate, userInfo]);

    // 未登录时先阻止页面内容挂载，避免 HomeUpload 提前请求 imputation quota 接口
    if (!userInfo) {
        return null;
    }

    if (profileLoading) {
        return (
            <div style={{ minHeight: 'calc(100vh - 120px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    const itemsMenu = [
        {
            label: 'New submission',
            key: 'search',
            icon: <FileSearchOutlined style={{ fontSize: '20px' }} />,
        },
        {
            label: 'My submissions',
            key: 'upload',
            icon: <TableOutlined style={{ fontSize: '20px' }} />,
        },
    ];

    const renderPage = () => {
        if (downPage === 'search') {
            return (<HomeUpload onPageChange={handlePageChange} />)
        } else if (downPage === 'upload') {
            return (<HomeSearchResult />)
        }
    }

    const handlePageChange = (page) => {
        setDownPage(page);
        setCurrent(page);
    };


    const menuClick = (e) => {
        setDownPage(e.key)
        setCurrent(e.key);
    };

    const workflowCardStyle = {
        height: '100%',
        borderRadius: '16px',
        border: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        textAlign: 'center',
        padding: '24px 16px',
        backgroundColor: 'white'
    };

    const stepIconStyle = {
        color: '#055878',
        fontSize: '48px',
        marginBottom: '20px',
        display: 'block'
    };

    return (
        <div className="shared-tool-page" style={{ paddingBottom: '80px', backgroundColor: '#fcfcfc' }}>
            {isAuditPending ? <AuditPendingNotice /> : <>
            
            {/* Header / Menu Section */}
            <div className="shared-tool-hero" style={{ backgroundColor: 'white', borderBottom: '1px solid #f0f0f0', marginBottom: '40px' }}>
                <Row justify="center">
                    <Col xs={24} sm={20} md={18}>
                        <Menu
                            className="imputation-switch-menu"
                            onClick={menuClick}
                            defaultSelectedKeys={['search']}
                            selectedKeys={[current]}
                            mode="horizontal"
                            items={itemsMenu}
                            style={{ 
                                fontSize: '18px', 
                                fontWeight: '600', 
                                border: 'none',
                                height: '64px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        />
                    </Col>
                </Row>
            </div>

            <Row justify="center">
                <Col xs={22} sm={20} md={18}>
                    {/* Main Content Area */}
                    <Card 
                        bordered={false} 
                        style={{ 
                            borderRadius: '20px', 
                            boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                            minHeight: '400px',
                            marginBottom: '60px',
                            overflow: 'hidden'
                        }}
                        bodyStyle={{ padding: '40px 20px' }}
                    >
                        {renderPage()}
                    </Card>

                    {/* Workflow Title */}
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <Title level={2} style={{ color: '#055878', fontWeight: 800 }}>
                            Analysis Workflow
                        </Title>
                        <Text type="secondary" style={{ fontSize: '16px' }}>
                            Submit a VCF file, select a reference panel, and download the imputed results.
                        </Text>
                    </div>

                    {/* Workflow Section */}
                    <Row gutter={[24, 24]}>
                        <Col xs={24} sm={8}>
                            <Card style={workflowCardStyle}>
                                <div style={{ 
                                    position: 'absolute', top: '10px', left: '10px', 
                                    backgroundColor: '#055878', color: 'white', 
                                    width: '30px', height: '30px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', fontSize: '14px'
                                }}>1</div>
                                <CloudUploadOutlined style={stepIconStyle} />
                                <Title level={4} style={{ color: '#055878', marginBottom: '12px' }}>Upload input VCF</Title>
                                <Text style={{ fontSize: '15px', color: '#666', lineHeight: '1.6' }}>
                                    Upload a compressed VCF file that meets the chromosome naming requirements.
                                </Text>
                            </Card>
                        </Col>

                        <Col xs={24} sm={8}>
                            <Card style={workflowCardStyle}>
                                <div style={{ 
                                    position: 'absolute', top: '10px', left: '10px', 
                                    backgroundColor: '#055878', color: 'white', 
                                    width: '30px', height: '30px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', fontSize: '14px'
                                }}>2</div>
                                <ShareAltOutlined style={stepIconStyle} />
                                <Title level={4} style={{ color: '#055878', marginBottom: '12px' }}>Phase and impute</Title>
                                <Text style={{ fontSize: '15px', color: '#666', lineHeight: '1.6' }}>
                                    The selected reference panel is used for pre-phasing and genotype imputation.
                                </Text>
                            </Card>
                        </Col>

                        <Col xs={24} sm={8}>
                            <Card style={workflowCardStyle}>
                                <div style={{ 
                                    position: 'absolute', top: '10px', left: '10px', 
                                    backgroundColor: '#055878', color: 'white', 
                                    width: '30px', height: '30px', borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', fontSize: '14px'
                                }}>3</div>
                                <CloudDownloadOutlined style={stepIconStyle} />
                                <Title level={4} style={{ color: '#055878', marginBottom: '12px' }}>Download results</Title>
                                <Text style={{ fontSize: '15px', color: '#666', lineHeight: '1.6' }}>
                                    Download imputed VCF files after the job is complete. Submitted files and results are removed after 14 days.
                                </Text>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
            </>}
        </div>
    );

}
